import SublineModel from '@/editor/entities/2d/sublineModel';
import {
  IAbsorbMeasureLine,
  IMeasureAbsorbPointInfo,
  IMeasureCalculateOptions,
  MeasureAbsorbCalculator
} from './measureAbsorbCalculator';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { EEntityType } from '@/editor/types';
import { getShape, getShapeSeg } from '@/editor/math/shape/other';
import { IFurniture } from '@/editor/data/model/furniture';
import { IAssemblyData } from '@/editor/data/assembly/assembly';
import { createBoxParallelAbsorbSegs } from '../box/utils';
import { genLineSegHash } from '@/editor/utils/hash';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { EColumnType, IColumnData } from '@/editor/data/model/model';
import { WallAbsorbPoint } from '@/editor/entities/2d/wallAbsorbPoint2';
import { modelManager } from '../../entityManager';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { SCENE_SIZE } from '@/editor/constant/scene';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { pixelRatio2d } from '@/editor/math/const';
import { EAbsorbPointType, EMeasureSubLineType } from '@/editor/types/absorb';
import { IShapePoint2d } from '@/editor/types/shape';
import WallStore from '@/editor/stores/WallStore';
import SeparateWallStore from '@/editor/stores/SeparateWallStore';
import { getLinearDimensionRulerLine, IAreaDimension } from '@/editor/data/dimension/dimension';
import { Line2d } from '@/editor/math/Line2d';
import { clipToScene, getAreaDimensionStatus } from './utils';
import { getOffsetLineSegByPoint } from '../wall/utils';

interface ICalculateAbsorbInfoOptions {
  allowRandomPoint?: boolean;
  showOrthogonalLine?: boolean;
}

interface ICalculateBoundingOptions {
  id: string;
  center: IPoint2d;
  width: number;
  thickness: number;
  rotation: number;
  type: EEntityType;
}

export interface IUpdataDataOptions {
  areaDimension?:
    | boolean
    | {
        enabled: boolean;
        excludeIds?: string[];
      };
  linearDimension?: boolean;
}

export class MeasureAbsorbManager {
  private _subLineModel = new SublineModel();
  private _calculator = new MeasureAbsorbCalculator();
  private _absorbPoint = new WallAbsorbPoint();
  private _lineHash: Set<string> = new Set();
  private _lineInfos: IAbsorbMeasureLine[] = [];
  private _eventDisposeSet: Set<() => void> = new Set();
  private _gs: TGlobalStores;
  private _zoom: number;

  constructor(gs: TGlobalStores) {
    this._zoom = gs.currentSceneZoom;
    this._gs = gs;
    this._eventDisposeSet
      .add(editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this._setZoom))
      .add(editorEvent.on(EditorEventName.ZOOM_UPDATE_3D, this._setZoom));
  }

  private _setZoom = () => {
    if (this._gs.currentSceneZoom !== this._zoom) {
      this._zoom = this._gs.currentSceneZoom;
      this._absorbPoint.updateZoom(this._zoom);
    }
  };

  private _boxBoundingLinesHandler({ id, type, ...options }: ICalculateBoundingOptions) {
    const result: IAbsorbMeasureLine[] = [];
    const rotatedBox = {
      center: options.center,
      width: options.width,
      thickness: options.thickness,
      rotation: options.rotation
    };

    const { boxBoundingLines } = createBoxParallelAbsorbSegs(rotatedBox);
    boxBoundingLines.forEach((boxBoundingLine) => {
      const { line, seg, bounds } = boxBoundingLine;
      // bounds 为整个box的包围盒，视口过滤时同一个box的四条边可见性一致
      result.push({ id, seg, shape: line, start: seg.start, end: seg.end, entityType: type, bounds });
    });

    return result;
  }

  private _shapePathLinesHandler({
    id,
    path,
    type,
    closed
  }: {
    id: string;
    path: IShapePoint2d[];
    type: EEntityType;
    closed: boolean;
  }) {
    const result: IAbsorbMeasureLine[] = [];
    const length = closed ? path.length : path.length - 1;
    if (length <= 0) return result;

    for (let i = 0; i < length; i++) {
      const start = path[i];
      const end = path[(i + 1) % path.length];
      if (!end) continue;

      const lineHash = start.center
        ? `${id}-${i}-${start.x}-${start.y}-${end.x}-${end.y}-${start.center.x}-${start.center.y}-${start.clockwise}`
        : String(genLineSegHash(start, end));
      if (this._lineHash.has(lineHash)) continue;

      this._lineHash.add(lineHash);
      const geometryInfo = {
        start,
        end,
        center: start.center,
        clockwise: start.clockwise
      };
      result.push({
        id,
        start,
        end,
        center: start.center,
        clockwise: start.clockwise,
        seg: getShapeSeg(geometryInfo),
        shape: getShape(geometryInfo),
        entityType: type
      });
    }

    return result;
  }

  private _formatFurnitureLineInfos(
    furnitureDatas: IFurniture[],
    assemblyDatas: IAssemblyData[],
    hiddenIds: Partial<Record<EEntityType, string[]>>
  ) {
    let lineInfos: IAbsorbMeasureLine[] = [];
    const excludeFurnitureSet = new Set(hiddenIds[EEntityType.Furniture]);
    const excludeAssemblySet = new Set(hiddenIds[EEntityType.Assembly]);

    for (const furniture of furnitureDatas) {
      if (excludeFurnitureSet.has(furniture.id)) continue;
      lineInfos = lineInfos.concat(this._boxBoundingLinesHandler({ ...furniture, type: EEntityType.Furniture }));
    }

    for (const assembly of assemblyDatas) {
      if (excludeAssemblySet.has(assembly.id)) continue;
      lineInfos = lineInfos.concat(this._boxBoundingLinesHandler({ ...assembly, type: EEntityType.Assembly }));
    }

    return lineInfos;
  }

  private _formatWallLineInfos(
    wallData: ReturnType<WallStore['getWallDatas']>,
    sepWallData: ReturnType<SeparateWallStore['getSepDatas']>,
    hiddenIds: Partial<Record<EEntityType, string[]>>
  ) {
    let lineInfos: IAbsorbMeasureLine[] = [];
    const excludeWallIds = new Set(hiddenIds[EEntityType.Wall]);
    const excludeSepWallIds = new Set(hiddenIds[EEntityType.SeparateWall]);

    for (const wall of wallData) {
      if (excludeWallIds.has(wall.id)) continue;

      for (const path of wall.drawWallInfo.drawShape) {
        lineInfos = lineInfos.concat(
          this._shapePathLinesHandler({
            id: wall.id,
            path,
            type: EEntityType.Wall,
            closed: path.length > 2
          })
        );
      }
    }

    for (const sepWall of sepWallData) {
      const { id, type, relativeWallId } = sepWall;
      if ((relativeWallId && excludeWallIds.has(relativeWallId)) || excludeSepWallIds.has(id)) continue;

      const entityType = type === ESeparateWallType.BaseVirtualWall ? EEntityType.Wall : EEntityType.SeparateWall;
      const path = sepWall.drawSeparateWallInfo.drawShape;
      lineInfos = lineInfos.concat(
        this._shapePathLinesHandler({
          id,
          path,
          type: entityType,
          closed: path.length > 2
        })
      );
    }

    return lineInfos;
  }

  private _formatColumnLineInfos(columnDatas: IColumnData[], hiddenIds: Partial<Record<EEntityType, string[]>>) {
    let lineInfos: IAbsorbMeasureLine[] = [];
    const excludeColumnSet = new Set(hiddenIds[EEntityType.Column]);

    for (const column of columnDatas) {
      if (excludeColumnSet.has(column.id)) continue;
      lineInfos = lineInfos.concat(
        this._boxBoundingLinesHandler({
          id: column.id,
          center: column.center,
          width: column.type === EColumnType.cylinder ? column.radius * 2 : column.width,
          thickness: column.type === EColumnType.cylinder ? column.radius * 2 : column.thickness,
          rotation: column.rotation,
          type: EEntityType.Column
        })
      );
    }

    return lineInfos;
  }

  private _formatAreaDimensionLineInfos(areaDimensionData: IAreaDimension[], excludeDimension: string[]) {
    const lineInfos: IAbsorbMeasureLine[] = [];
    const excludeAreaDimensionSet = new Set(excludeDimension);
    for (const areaDimension of areaDimensionData) {
      if (excludeAreaDimensionSet.has(areaDimension.id)) continue;

      for (let i = 0; i < areaDimension.points.length; i++) {
        const start = areaDimension.points[i];
        const end = areaDimension.points[(i + 1) % areaDimension.points.length];
        const seg = new LineSeg2d(start, end);
        lineInfos.push({
          id: `${start.id}-${end.id}`,
          start,
          end,
          seg,
          shape: seg.createLine()
        });
      }
    }

    return lineInfos;
  }

  public updateLinearDimensionLineInfos() {
    const lineInfos: IAbsorbMeasureLine[] = [];
    const linearDimensionData = this._gs.dimensionStore.getLinearDimList();
    for (const linearDimension of linearDimensionData) {
      const { start, end } = linearDimension;
      const seg = new LineSeg2d(start, end);
      lineInfos.push({
        id: `${start.x}-${start.y}-${end.x}-${end.y}`,
        start,
        end,
        seg,
        shape: seg.createLine(),
        effectiveOnEndpoint: true
      });

      const { start: rulerLineStart, end: rulerLineEnd } = getLinearDimensionRulerLine(linearDimension);
      const rulerLine = new LineSeg2d(rulerLineStart, rulerLineEnd);
      lineInfos.push({
        id: `${rulerLineStart.x}-${rulerLineStart.y}-${rulerLineEnd.x}-${rulerLineEnd.y}`,
        start: rulerLineStart,
        end: rulerLineEnd,
        seg: rulerLine,
        shape: rulerLine.createLine(),
        entityType: EEntityType.LinearDimension
      });
    }
    this._calculator.updateData(this._lineInfos.concat(lineInfos));
  }

  public updateFurnitureLineInfos() {
    const hiddenIds = this._gs.visibilityStore.getHiddenEntitiesIds();
    const furnitureLineInfos = this._formatFurnitureLineInfos(
      this._gs.modelStore.getFurnitureDatas(),
      this._gs.assemblyStore.getAssemblyDatas(),
      hiddenIds
    );

    this._calculator.updateData(this._lineInfos.concat(furnitureLineInfos));
  }

  updateBaseData(options?: IUpdataDataOptions) {
    this._lineHash.clear();
    this._lineInfos = [];
    let lineInfos: IAbsorbMeasureLine[] = [];
    const hiddenIds = this._gs.visibilityStore.getHiddenEntitiesIds();

    // 添加墙体/隔墙/边界边线
    const wallLineInfos = this._formatWallLineInfos(
      this._gs.wallStore.getWallDatas(),
      this._gs.separateWallStore.getSepDatas(),
      hiddenIds
    );
    lineInfos = lineInfos.concat(wallLineInfos);

    // 添加柱边线
    const columnLineInfos = this._formatColumnLineInfos(this._gs.structStore.getColumnDatas(), hiddenIds);
    lineInfos = lineInfos.concat(columnLineInfos);

    const { enabled: isEnabledAreaDimension, excludeIds } = getAreaDimensionStatus(options);
    if (isEnabledAreaDimension) {
      // 处理面积标注
      const areaDimensionLineInfo = this._formatAreaDimensionLineInfos(
        this._gs.dimensionStore.getAreaDimList(),
        excludeIds
      );
      lineInfos = lineInfos.concat(areaDimensionLineInfo);
    }
    this._lineInfos = [...lineInfos];

    this._calculator.updateData(this._lineInfos);
  }

  setOrthogonalData(point: IPoint2d) {
    this._calculator.setOrthogonalData(point);
  }

  clearOrthogonalData() {
    this._calculator.clearOrthogonalData();
  }

  setNormalData(startPoint: IPoint2d, endPoint: IPoint2d) {
    this._calculator.setNormalData(startPoint, endPoint);
  }

  clearNormalData() {
    this._calculator.clearNormalData();
  }

  private _dxfAdsorbSegHandler(dxfLine: number[] | undefined) {
    if (!dxfLine) return undefined;
    const start = { x: dxfLine[0], y: dxfLine[1] };
    const end = { x: dxfLine[2], y: dxfLine[3] };
    const seg = new LineSeg2d(start, end);
    if (seg.length > 200) {
      // TODO: 待优化，最好在dxf解析时过滤掉
      const seg = new LineSeg2d(start, end);
      return [
        {
          id: '',
          start,
          end,
          seg,
          shape: seg.createLine(),
          entityType: EEntityType.FloorPlan
        }
      ];
    }
    return undefined;
  }

  private _calculateDxfAdsorb(rawPoint: IPoint2d) {
    if (globalStores.paramStore.floorPlanConfig?.type === 'dxf') {
      const dxfLine = modelManager.floorPlanManager.getNearestLine(
        rawPoint.x,
        rawPoint.y,
        globalStores.dynamicAbsorbDistance
      );
      return this._dxfAdsorbSegHandler(dxfLine);
    }
  }

  calculateAbsorbPointThenRender(rawPoint: IPoint2d) {
    this.clearModel();
    const calculateOptions: IMeasureCalculateOptions = {};
    calculateOptions.extraWallInfos = this._calculateDxfAdsorb(rawPoint);

    const absorbPointInfo = this._calculator.calculate(rawPoint, calculateOptions);
    if (!absorbPointInfo) return;

    this.updateModel(absorbPointInfo);

    return absorbPointInfo.absorbPoint;
  }

  calculateAbsorbPointByLinearThenRender(startPoint: IPoint2d, endPoint: IPoint2d, rawPoint: IPoint2d) {
    this.clearModel();

    // 计算新的线段
    const shadowLinePosition = getOffsetLineSegByPoint(startPoint, endPoint, rawPoint);

    const absorbPointInfo = this._calculator.calculateAbsorbByLinearDimension(
      shadowLinePosition.start,
      shadowLinePosition.end
    );
    if (!absorbPointInfo) return;

    this.updateModel(absorbPointInfo);

    return absorbPointInfo.absorbPoint;
  }

  calculateAbsorbInfoThenRender(rawPoint: IPoint2d, options?: ICalculateAbsorbInfoOptions) {
    this.clearModel();
    const calculateOptions: IMeasureCalculateOptions = {};
    calculateOptions.extraWallInfos = this._calculateDxfAdsorb(rawPoint);

    const absorbPointInfo = this._calculator.calculate(rawPoint, calculateOptions);
    if (!absorbPointInfo) return;

    const sublineInfos = Array.from(absorbPointInfo.sublineInfos);
    const legal = options?.allowRandomPoint || !!Array.from(sublineInfos).find((item) => item?.entityType);
    let absorbEntity: { type: EEntityType; id: string } | null = null;

    for (const subline of sublineInfos) {
      if (subline.entityType && absorbEntity?.type !== EEntityType.Furniture) {
        absorbEntity = { type: subline.entityType, id: subline.id };
      }
    }

    this.updateModel(absorbPointInfo, legal, options?.showOrthogonalLine);

    return { absorbPoint: absorbPointInfo.absorbPoint, legal, absorbEntity };
  }

  private updateModel(subLineInfos: IMeasureAbsorbPointInfo, legal?: boolean, allowOrthogonalLine?: boolean) {
    const subLines: { start: IPoint2d; end: IPoint2d; center?: IPoint2d; clockwise?: boolean }[] = [];
    const absorbPoint = subLineInfos.absorbPoint;

    subLineInfos.sublineInfos.forEach((item) => {
      switch (item.type) {
        case EMeasureSubLineType.HorizontalLine: {
          if (allowOrthogonalLine) {
            const size = SCENE_SIZE * pixelRatio2d;
            const minX = Math.min(-size, absorbPoint.x - size / 100);
            const maxX = Math.max(size, absorbPoint.x + size / 100);
            subLines.push({
              start: { x: minX, y: absorbPoint.y },
              end: { x: maxX, y: absorbPoint.y }
            });
          }

          break;
        }
        case EMeasureSubLineType.VerticalLine: {
          if (allowOrthogonalLine) {
            const size = SCENE_SIZE * pixelRatio2d;
            const minY = Math.min(-size, absorbPoint.y - size / 100);
            const maxY = Math.max(size, absorbPoint.y + size / 100);
            subLines.push({
              start: { x: absorbPoint.x, y: minY },
              end: { x: absorbPoint.x, y: maxY }
            });
          }
          break;
        }
        case EMeasureSubLineType.MidNormalLine: {
          const shape = item.shape as Line2d;
          const [p1, p2] = shape.getPoints();
          const dir = { x: p2.x - p1.x, y: p2.y - p1.y };
          const [pStart, pEnd] = clipToScene(absorbPoint, dir);
          subLines.push({
            start: pStart,
            end: pEnd
          });
          break;
        }
        case EMeasureSubLineType.EntityBoxExtensionLine: {
          if (item.origin) {
            subLines.push({
              start: item.origin,
              end: absorbPoint
            });
          }
          break;
        }
      }
    });

    this._subLineModel.update(subLines);
    if (legal === false || subLineInfos.absorbPointType === EAbsorbPointType.Line) this._absorbPoint.clear();
    else this._absorbPoint.update(absorbPoint, this._zoom, subLineInfos.absorbPointType);
  }

  clearModel() {
    this._subLineModel.clear();
    this._absorbPoint.clear();
  }

  dispose() {
    this.clearModel();
    this._eventDisposeSet.forEach((dispose) => dispose());
    this._eventDisposeSet.clear();
  }
}
